import { IconWrapper } from "features/trades/common/styles";
import Dropdown from "common/Dropdown/Dropdown";
import React, { useContext } from "react";
import DeleteOutlined from "@ant-design/icons/DeleteOutlined";
import EditOutlined from "@ant-design/icons/EditOutlined";
import EllipsisOutlined from "@ant-design/icons/EllipsisOutlined";
import PauseCircleOutlined from "@ant-design/icons/PauseCircleOutlined";
import PlayCircleOutlined from "@ant-design/icons/PlayCircleOutlined";
import styled, { useTheme } from "styled-components";
import NewLookKebab from "features/portfolio/NewLook/NewLookKebab.tsx";
import { CREATE_ACCOUNT_PATH } from "common/constants";
import { ItemType } from "antd/es/menu/interface";
import { ClientData, LanguageNameSpaces } from "@shared/types";
import { useAccountActions } from "@shared/features/configuration/AccountConfiguration/hooks";
import DeactivateAccountModal from "features/configuration/accounts/AccountConfigurationDrawer/DeactivateAccountModal";
import { displayResultMessage, openMessage } from "utils/misc";
import useQuotaModal from "features/settings/Quota/useQuotaModal";
import { useHistory } from "react-router-dom";
import Tooltip from "common/Tooltips/Tooltip";
import { DrawerContext } from "common/Drawers/Drawer.context.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useSubUserPermissions } from "@shared/utils/hooks/misc.ts";

interface AccountActionsProps {
  account?: ClientData;
  accountId?: number;
  closeDrawer?: () => void;
  iconSize?: number;
}

const AccountActions = ({
  account: propsAccount,
  accountId: propsAccountId,
  closeDrawer,
  iconSize,
}: AccountActionsProps) => {
  const { isDrawer } = useContext(DrawerContext) || {};
  const { isNewLook } = useTheme();
  const history = useHistory();
  const accountId = propsAccountId ?? propsAccount?.client_id;
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const { isSubUser, isReadOnly } = useSubUserPermissions();

  const handleEditClick = () => {
    const updatedQuery = new URLSearchParams();
    if (accountId) updatedQuery.set("account", accountId.toString());
    history.push(`${CREATE_ACCOUNT_PATH}?${updatedQuery.toString()}`);
  };

  const {
    deactivateModalData,
    quotaModal,
    onDeleteClick,
    onEditClick,
    changeStatusButtonData,
    account,
    accountIsReadOnly,
    accountIsSubUserDeleted,
  } = useAccountActions({
    account: propsAccount,
    accountId,
    displayResultMessage,
    useQuotaModal,
    handleEditClick,
    openMessage,
  });

  const actionsItems: ItemType[] = [
    {
      key: "delete",
      icon: <DeleteOutlined />,
      label: t("actions.delete"),
      onClick: onDeleteClick,
    },
  ];
  if (!isReadOnly) {
    actionsItems.push({
      key: "changeStatus",
      icon: changeStatusButtonData.isPauseIcon ? (
        <PauseCircleOutlined />
      ) : (
        <PlayCircleOutlined />
      ),
      label: changeStatusButtonData.text,
      onClick: changeStatusButtonData.onClick,
      className: accountIsReadOnly ? "invalid" : undefined,
    });
  }
  if (!isSubUser) {
    actionsItems.splice(1, 0, {
      key: "edit",
      icon: <EditOutlined />,
      label: t("actions.edit"),
      onClick: onEditClick,
      className:
        accountIsReadOnly || accountIsSubUserDeleted ? "invalid" : undefined,
    });
  }

  const getTrigger = () => {
    // New look: the same quiet round trigger and line icon as the bot menus.
    const icon = isNewLook ? (
      <NewLookKebab
        horizontal={isDrawer}
        aria-label={t("actions.more_options")}
      />
    ) : (
      <IconWrapper>
        <StyledEllipsisOutlined
          $iconSize={iconSize}
          rotate={isDrawer ? 90 : 0}
        />
      </IconWrapper>
    );
    if (!isDrawer) {
      return <Tooltip title={t("actions.more_options")}>{icon}</Tooltip>;
    }
    return icon;
  };

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Dropdown
        title={t("actions.account_actions")}
        menu={{ items: actionsItems }}
        trigger={["click"]}
      >
        {getTrigger()}
      </Dropdown>
      {quotaModal}
      <DeactivateAccountModal
        accountStatus={deactivateModalData.accountStatus}
        onCancel={deactivateModalData.onCancel}
        onDelete={closeDrawer}
        account={account}
      />
    </div>
  );
};

export default AccountActions;

const StyledEllipsisOutlined = styled(EllipsisOutlined)<{ $iconSize?: number }>`
  transform: rotate(90deg);
  font-size: ${(p) => p.$iconSize || 30}px;
  cursor: pointer;
  color: ${(p) => p.theme.colors.basic};
`;
